/**
 * BOL review queue — the human step when automatic linking gives up, plus
 * the portal-triggered shipping flow.
 *
 * Scanned Bills of Lading are normally linked to their order automatically
 * (barcode first, then OCR + verification, then an LLM fallback). Anything
 * the pipeline could not resolve lands here for a person.
 *
 * When the shipping flow is on, a verified link (barcode or manual) calls
 * the sales site's bolScanProcess — the same flow as the warehouse scan gun.
 * The sales site may answer NEEDS INPUT (e.g. the carrier wants a tracking
 * number); those land here as cards a person completes, and every ship the
 * portal caused is confirmed here too.
 */
import { useCallback, useEffect, useState } from 'react';

import { useCurrentUser } from '../context/AuthContext';
import { bolReviewService } from '../services/bolReview';
import { getDefaultLandingPage } from '../utils/landingPage';
import type {
  BolDismissReason,
  BolReviewItem,
  BolScanCompleteInput,
  BolScanTriggerItem,
  ShippingFlowState,
} from '../services/bolReview';

const DISMISS_REASONS: { value: BolDismissReason; label: string }[] = [
  { value: 'not_a_bol', label: 'Not a BOL' },
  { value: 'blank_page', label: 'Blank page' },
  { value: 'duplicate', label: 'Duplicate' },
  { value: 'illegible', label: 'Illegible — reshoot' },
  { value: 'other', label: 'Other' },
];

/** Label + input shape for each field the sales site can ask for. */
const NEED_FIELDS: Record<string, { label: string; money: boolean }> = {
  tracking_no: { label: 'Tracking / PRO #', money: false },
  freight_costs: { label: 'Freight cost $', money: true },
  fuel_surcharges: { label: 'Fuel surcharge $', money: true },
  drop_off_charge: { label: 'Drop-off charge $', money: true },
};

const NEED_ORDER = ['tracking_no', 'freight_costs', 'fuel_surcharges', 'drop_off_charge'];

function pageLabel(item: BolScanTriggerItem): string {
  const name = item.filename ?? 'scanned BOL';
  return item.page_number ? `${name} · page ${item.page_number}` : name;
}

function apiDetail(err: unknown, fallback: string): string {
  return (
    (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? fallback
  );
}

/** True when the API refused the call because the caller is not an admin. */
function isForbidden(err: unknown): boolean {
  return (err as { response?: { status?: number } })?.response?.status === 403;
}

/**
 * What a signed-in non-admin sees instead of the queue. Every endpoint behind
 * this page is ``require_admin`` (app/routes/order_bols.py), so a CSR who
 * follows a link here would otherwise get two generic "could not load" lines
 * with no hint that the page was never theirs.
 */
function AdminAccessRequired({ home }: { home: string }) {
  return (
    <div
      role="alert"
      data-testid="bol-review-access-denied"
      style={{
        border: '1px solid #fcd34d',
        background: '#fffbeb',
        borderRadius: 8,
        padding: 16,
        maxWidth: 560,
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 6 }}>Admin access required</div>
      <p style={{ margin: '0 0 10px', color: '#475569', fontSize: 14 }}>
        The BOL review queue is an admin tool for linking scanned Bills of Lading that the
        automatic matcher could not place. Your login can&apos;t open it. If a scan for one
        of your orders needs freight or tracking details, the order itself carries that
        prompt on its Freight row.
      </p>
      <a href={home} style={{ color: '#2563eb', fontSize: 14 }}>
        Back to your home page
      </a>
    </div>
  );
}

function shippedSummary(item: BolScanTriggerItem): string {
  const how =
    item.reason === 'barcode_link'
      ? 'Order barcode read (Code 39)'
      : item.reason === 'manual_link'
        ? 'Linked by hand'
        : 'Completed here';
  const pro = item.tracking_no ? ` · carrier PRO ${item.tracking_no} read` : '';
  const when = new Date(item.completed_at ?? item.created_at).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });
  if (item.status === 'already_processed') {
    return `${how}${pro} · sent to the sales site at ${when} → already processed by the warehouse gun; no second ship.`;
  }
  return `${how}${pro} · sent to the sales site at ${when} → invoice queued, order marked Shipped, customer shipped email sent. Same result as the warehouse scan gun; if the gun also scans this BOL, the second call is a no-op.`;
}

const badge = (bg: string, fg: string): React.CSSProperties => ({
  display: 'inline-block',
  background: bg,
  color: fg,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 0.4,
  padding: '3px 8px',
  borderRadius: 999,
  marginRight: 8,
  verticalAlign: 'middle',
});

const inputStyle: React.CSSProperties = {
  padding: '6px 10px',
  border: '1px solid #cbd5e1',
  borderRadius: 6,
  fontSize: 13,
};

const primaryButton: React.CSSProperties = {
  padding: '6px 14px',
  borderRadius: 6,
  border: 'none',
  background: '#2563eb',
  color: '#fff',
  fontSize: 13,
  cursor: 'pointer',
};

const secondaryButton: React.CSSProperties = {
  padding: '6px 14px',
  borderRadius: 6,
  border: '1px solid #cbd5e1',
  background: '#fff',
  color: '#0f172a',
  fontSize: 13,
  cursor: 'pointer',
};

export default function BolReviewPage() {
  const user = useCurrentUser();
  // Known up front when an AuthProvider is present; otherwise the server's
  // 403 on the first load flips it (see isForbidden).
  const [accessDenied, setAccessDenied] = useState(
    () => user != null && user.role !== 'ADMIN',
  );
  const home = user ? getDefaultLandingPage(user) : '/portal';

  const [items, setItems] = useState<BolReviewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [rowError, setRowError] = useState<Record<string, string>>({});
  const [linked, setLinked] = useState<Record<string, string>>({});
  const [dismissing, setDismissing] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<string[]>([]);

  // Shipping flow (bolScanProcess)
  const [flow, setFlow] = useState<ShippingFlowState | null>(null);
  const [needsInput, setNeedsInput] = useState<BolScanTriggerItem[]>([]);
  const [shipped, setShipped] = useState<BolScanTriggerItem[]>([]);
  const [flowError, setFlowError] = useState<string | null>(null);
  const [needDrafts, setNeedDrafts] = useState<Record<string, Record<string, string>>>({});
  const [skipTracking, setSkipTracking] = useState<Record<string, boolean>>({});
  const [completing, setCompleting] = useState<string | null>(null);
  const [triggerError, setTriggerError] = useState<Record<string, string>>({});

  const loadTriggers = useCallback(async () => {
    setFlowError(null);
    try {
      const data = await bolReviewService.listScanTriggers();
      setFlow(data.shipping_flow);
      setNeedsInput(data.needs_input ?? []);
      setShipped(data.shipped ?? []);
    } catch (err: unknown) {
      if (isForbidden(err)) {
        setAccessDenied(true);
        return;
      }
      setFlowError('Could not load the shipping-flow status.');
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await bolReviewService.listNeedsReview();
      setItems(data.items ?? []);
    } catch (err: unknown) {
      if (isForbidden(err)) {
        setAccessDenied(true);
        return;
      }
      setError('Could not load the review queue.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (accessDenied) return;
    void load();
    void loadTriggers();
  }, [accessDenied, load, loadTriggers]);

  const link = async (id: string) => {
    const value = (drafts[id] ?? '').trim();
    if (!value) return;
    setSaving(id);
    setRowError((p) => ({ ...p, [id]: '' }));
    try {
      const data = await bolReviewService.link(id, value);
      setLinked((p) => ({ ...p, [id]: data.sage_order_no ?? value }));
      setItems((prev) => prev.filter((i) => i.id !== id));
      // A link may have caused a shipping-flow attempt: refresh the cards.
      if (data.scan_trigger) void loadTriggers();
    } catch (err: unknown) {
      setRowError((p) => ({ ...p, [id]: apiDetail(err, 'Could not link this BOL.') }));
    } finally {
      setSaving(null);
    }
  };

  const dismiss = async (id: string, reason: BolDismissReason) => {
    setDismissing(id);
    setRowError((p) => ({ ...p, [id]: '' }));
    try {
      await bolReviewService.dismiss(id, reason);
      setDismissed((p) => [...p, id]);
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch (err: unknown) {
      setRowError((p) => ({ ...p, [id]: apiDetail(err, 'Could not dismiss this BOL.') }));
    } finally {
      setDismissing(null);
    }
  };

  /**
   * 'freight_quote_failed' → "the live carrier rate lookup failed";
   * 'no_rate_tool' → the PORTAL held the scan before calling (kanban e03d39d2).
   */
  const reasonLabel = (reason: string | null): string => {
    if (!reason) return 'no live rate for this carrier';
    if (reason === 'freight_quote_failed') return 'the live carrier rate lookup failed';
    if (reason === 'no_rate_tool') {
      return 'this carrier has no rate tool on the sales site, so the portal held the scan before calling it (freight keyed on the Sage sales order releases it on its own)';
    }
    return reason.replace(/_/g, ' ');
  };

  const neededFields = (item: BolScanTriggerItem): string[] =>
    NEED_ORDER.filter((n) => (item.needs ?? []).includes(n));

  const canComplete = (item: BolScanTriggerItem): boolean => {
    const values = needDrafts[item.id] ?? {};
    return neededFields(item).every((n) => {
      if (n === 'tracking_no' && skipTracking[item.id]) return true;
      return (values[n] ?? '').trim() !== '';
    });
  };

  const completeShip = async (item: BolScanTriggerItem) => {
    const values = needDrafts[item.id] ?? {};
    const body: BolScanCompleteInput = {};
    for (const n of neededFields(item)) {
      const raw = (values[n] ?? '').trim();
      if (n === 'tracking_no') {
        if (skipTracking[item.id]) body.skip_tracking = true;
        else body.tracking_no = raw;
      } else if (raw !== '') {
        const num = Number(raw.replace(/[^0-9.]/g, ''));
        if (n === 'freight_costs') body.freight_costs = num;
        if (n === 'fuel_surcharges') body.fuel_surcharges = num;
        if (n === 'drop_off_charge') body.drop_off_charge = num;
      }
    }
    setCompleting(item.id);
    setTriggerError((p) => ({ ...p, [item.id]: '' }));
    try {
      const child = await bolReviewService.completeScanTrigger(item.id, body);
      if (child.status === 'failed') {
        setTriggerError((p) => ({
          ...p,
          [item.id]: child.error ?? 'The sales site did not accept the ship.',
        }));
      }
      await loadTriggers();
    } catch (err: unknown) {
      setTriggerError((p) => ({
        ...p,
        [item.id]: apiDetail(err, 'Could not complete the ship.'),
      }));
    } finally {
      setCompleting(null);
    }
  };

  const scannedWithGun = async (item: BolScanTriggerItem) => {
    setCompleting(item.id);
    setTriggerError((p) => ({ ...p, [item.id]: '' }));
    try {
      await bolReviewService.dismissScanTrigger(item.id, 'Scanned with the gun instead');
      setNeedsInput((prev) => prev.filter((t) => t.id !== item.id));
    } catch (err: unknown) {
      setTriggerError((p) => ({
        ...p,
        [item.id]: apiDetail(err, 'Could not mark this as handled.'),
      }));
    } finally {
      setCompleting(null);
    }
  };

  if (accessDenied) {
    return (
      <div style={{ padding: 24, maxWidth: 1000 }}>
        <h1 style={{ fontSize: 22, marginBottom: 12 }}>BOL review queue</h1>
        <AdminAccessRequired home={home} />
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 1000 }}>
      <h1 style={{ fontSize: 22, marginBottom: 4 }}>BOL review queue</h1>

      {flow && (
        <p
          data-testid="shipping-flow-state"
          style={{ color: '#64748b', fontSize: 12, marginTop: 0, marginBottom: 6 }}
        >
          Shipping flow:{' '}
          <strong
            style={{
              color: flow.enabled ? '#15803d' : flow.mode === 'shadow' ? '#2563eb' : '#b45309',
            }}
          >
            {flow.enabled ? 'ON' : flow.mode === 'shadow' ? 'SHADOW' : 'OFF'}
          </strong>
          {flow.enabled
            ? ' — a verified BOL link triggers the sales-site ship (bolScanProcess)'
            : flow.mode === 'shadow'
              ? ' — each link records what would be sent and when; nothing is sent to the sales site and the warehouse gun keeps shipping'
              : ' — links are recorded only; nothing is sent to the sales site'}
          {' · target: '}
          <span title={flow.target_url}>
            {flow.target_env === 'prod' ? 'live sales site (/ss/)' : 'dev sales site (/ssdev/)'}
          </span>
          {' · switched with BOL_SCAN_TRIGGER_MODE.'}
          {flow.refusal && (
            <span style={{ color: '#dc2626' }}> {flow.refusal}</span>
          )}
        </p>
      )}
      {flowError && <p style={{ color: '#dc2626', fontSize: 12 }}>{flowError}</p>}

      <p style={{ color: '#64748b', fontSize: 14, marginTop: 0 }}>
        Scans the automatic linker could not resolve. Open the PDF, read the
        order number off it, and enter it below. The number is verified against
        real orders before it links, so a typo is rejected rather than saved.
        If a page is not a Bill of Lading at all, dismiss it with a reason —
        nothing is deleted, and it stops being work.
      </p>

      {needsInput.map((item) => {
        const failed = item.status === 'failed';
        const fields = neededFields(item);
        return (
          <div
            key={item.id}
            data-testid={`needs-input-${item.id}`}
            style={{
              border: `1px solid ${failed ? '#fecaca' : '#fcd34d'}`,
              background: failed ? '#fef2f2' : '#fffbeb',
              borderRadius: 8,
              padding: 14,
              marginBottom: 12,
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              <span style={failed ? badge('#dc2626', '#fff') : badge('#d97706', '#fff')}>
                {failed ? 'FAILED' : 'NEEDS INPUT'}
              </span>
              {pageLabel(item)} → <span>{item.sage_order_no}</span>
              {item.customer_name && <span> {item.customer_name}</span>}
              {item.ship_via && <span> ({item.ship_via})</span>}
            </div>
            <div style={{ color: '#64748b', fontSize: 12, marginBottom: 10 }}>
              {failed
                ? `The sales site call failed: ${item.error ?? 'unknown error'}. Retry, or mark it handled if the gun scanned it.`
                : 'Order linked from the barcode, but the shipping flow needs a value the scan could not read. Fill it in and the portal completes the ship for you.'}
            </div>
            {!failed && (item.needs_reason || item.needs_detail) && (
              <div
                data-testid={`needs-reason-${item.id}`}
                style={{ color: '#92400e', fontSize: 12, marginTop: -6, marginBottom: 10 }}
              >
                {item.needs_reason === 'no_rate_tool' ? 'Portal hold' : 'Sales site'}:{' '}
                {reasonLabel(item.needs_reason)}
                {item.needs_detail ? ` — ${item.needs_detail}` : ''}.
                {item.red_flag_id
                  ? ' The order desk has been alerted; a CSR can also enter the freight on the order.'
                  : ''}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {fields.map((n) => (
                <label key={n} style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'center' }}>
                  {NEED_FIELDS[n].label}
                  <input
                    aria-label={`${NEED_FIELDS[n].label} for ${item.sage_order_no}`}
                    value={needDrafts[item.id]?.[n] ?? ''}
                    disabled={n === 'tracking_no' && !!skipTracking[item.id]}
                    onChange={(e) =>
                      setNeedDrafts((p) => ({
                        ...p,
                        [item.id]: { ...(p[item.id] ?? {}), [n]: e.target.value },
                      }))
                    }
                    placeholder={
                      n === 'tracking_no'
                        ? `${item.ship_via ?? 'Carrier'} PRO number`
                        : '0.00'
                    }
                    inputMode={NEED_FIELDS[n].money ? 'decimal' : 'text'}
                    style={{ ...inputStyle, width: NEED_FIELDS[n].money ? 110 : 200 }}
                  />
                </label>
              ))}
              {fields.includes('tracking_no') && (
                <label style={{ fontSize: 12, color: '#64748b', display: 'flex', gap: 4, alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    aria-label={`Ship ${item.sage_order_no} without a tracking number`}
                    checked={!!skipTracking[item.id]}
                    onChange={(e) =>
                      setSkipTracking((p) => ({ ...p, [item.id]: e.target.checked }))
                    }
                  />
                  no tracking number
                </label>
              )}
              <button
                type="button"
                onClick={() => void completeShip(item)}
                disabled={completing === item.id || !canComplete(item)}
                style={primaryButton}
              >
                {completing === item.id ? 'Sending…' : failed && fields.length === 0 ? 'Retry' : 'Complete ship'}
              </button>
              <button
                type="button"
                onClick={() => void scannedWithGun(item)}
                disabled={completing === item.id}
                style={secondaryButton}
              >
                Scanned with the gun instead
              </button>
            </div>
            {triggerError[item.id] && (
              <div style={{ color: '#dc2626', fontSize: 12, marginTop: 8 }}>
                {triggerError[item.id]}
              </div>
            )}
          </div>
        );
      })}

      {shipped.map((item) => (
        <div
          key={item.id}
          data-testid={`shipped-${item.id}`}
          style={{
            border: '1px solid #bbf7d0',
            background: '#f0fdf4',
            borderRadius: 8,
            padding: 14,
            marginBottom: 12,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            <span style={badge('#15803d', '#fff')}>
              {item.status === 'already_processed' ? 'ALREADY SHIPPED' : 'SHIPPED VIA PORTAL'}
            </span>
            {pageLabel(item)} → <span style={{ color: '#15803d' }}>{item.sage_order_no}</span>
            {item.customer_name && <span> {item.customer_name}</span>}
          </div>
          <div style={{ color: '#475569', fontSize: 12 }}>{shippedSummary(item)}</div>
        </div>
      ))}

      {loading && <p>Loading…</p>}
      {error && <p style={{ color: '#dc2626' }}>{error}</p>}

      {!loading && !error && items.length === 0 && (
        <p style={{ color: '#16a34a', fontWeight: 600 }}>
          Nothing waiting — every scanned BOL is linked.
        </p>
      )}

      {items.map((item) => (
        <div
          key={item.id}
          style={{
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            padding: 14,
            marginBottom: 12,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 2 }}>
            {item.display_name || item.filename}
          </div>
          {item.display_name && item.display_name !== item.filename && (
            <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 2 }}>
              scan file: {item.filename}
              {item.page_number ? ` · page ${item.page_number}` : ''}
            </div>
          )}
          <div style={{ color: '#64748b', fontSize: 12, marginBottom: 10 }}>
            {item.extraction_attempts} automatic attempt
            {item.extraction_attempts === 1 ? '' : 's'}
            {item.sage_order_no ? ` · unverified guess: ${item.sage_order_no}` : ''}
            {' · scanned '}
            {new Date(item.drive_modified_time).toLocaleString()}
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {/* Through our authenticated proxy (served from our GCS copy) —
                never the Drive viewer, which needs Drive folder access. */}
            <button
              type="button"
              onClick={() => {
                void bolReviewService.openPdf(item.id).catch(() =>
                  setError('Could not open the PDF. Please try again.'),
                );
              }}
              style={{
                fontSize: 13,
                color: '#2563eb',
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              Open PDF
            </button>
            <input
              value={drafts[item.id] ?? ''}
              onChange={(e) =>
                setDrafts((p) => ({ ...p, [item.id]: e.target.value }))
              }
              placeholder="Order number (e.g. AU01644)"
              style={{ ...inputStyle, width: 220 }}
            />
            <button
              onClick={() => void link(item.id)}
              disabled={saving === item.id || !(drafts[item.id] ?? '').trim()}
              style={primaryButton}
            >
              {saving === item.id ? 'Linking…' : 'Link'}
            </button>

            <select
              aria-label={`Dismiss ${item.filename}`}
              value=""
              disabled={dismissing === item.id}
              onChange={(e) => {
                const reason = e.target.value as BolDismissReason;
                if (reason) void dismiss(item.id, reason);
              }}
              style={{
                marginLeft: 'auto',
                padding: '6px 10px',
                border: '1px solid #cbd5e1',
                borderRadius: 6,
                fontSize: 13,
                background: '#fff',
                cursor: 'pointer',
              }}
            >
              <option value="">
                {dismissing === item.id ? 'Dismissing…' : 'Not a BOL…'}
              </option>
              {DISMISS_REASONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>

          {flow?.enabled && (
            <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 8 }}>
              Linking here also triggers the shipping flow (same as a barcode link).
            </div>
          )}

          {rowError[item.id] && (
            <div style={{ color: '#dc2626', fontSize: 12, marginTop: 8 }}>
              {rowError[item.id]}
            </div>
          )}
        </div>
      ))}

      {Object.keys(linked).length > 0 && (
        <p style={{ color: '#16a34a', fontSize: 13 }}>
          Linked this session: {Object.values(linked).join(', ')}
        </p>
      )}

      {dismissed.length > 0 && (
        <p style={{ color: '#64748b', fontSize: 13 }}>
          Dismissed this session: {dismissed.length}
        </p>
      )}
    </div>
  );
}
